// src/lib/supabase/cookie-options.ts

/**
 * Auth-cookie options shared by every cookie-bearing Supabase factory
 * (client.ts, server.ts createClient, middleware.ts updateSession).
 * MUST be identical across all three — a mismatch produces duplicate
 * competing sb-* cookies and half-logged-in states.
 *
 * NEXT_PUBLIC_AUTH_COOKIE_DOMAIN:
 *   prod  → ".playpulse.io" (one login across app./club. subdomains)
 *   dev   → unset (localhost is host-only; no cross-domain auth locally)
 *
 * HOST-AWARE: browsers REJECT a Set-Cookie whose Domain doesn't cover the
 * page host (e.g. Domain=.playpulse.io on a response served to any other
 * host), which would silently break login on that host (no session cookie at
 * all). So callers pass the current hostname and the domain only applies when
 * the host belongs to it; any other host falls back to a host-only cookie.
 *
 * Spec: tasks/2026-06-12-0016-club-portal-shell/spec.md §2
 */
export function getSharedCookieOptions(hostname?: string): { domain?: string } {
  const domain = process.env.NEXT_PUBLIC_AUTH_COOKIE_DOMAIN
  if (!domain) return {}
  if (hostname !== undefined && !hostMatchesCookieDomain(hostname, domain)) return {}
  return { domain }
}

/** True when `hostname` is the cookie domain's apex or a subdomain of it. */
export function hostMatchesCookieDomain(hostname: string, domain: string): boolean {
  const dom = domain.startsWith('.') ? domain : `.${domain}`
  const host = hostname.toLowerCase()
  return host === dom.slice(1) || host.endsWith(dom)
}

/**
 * One-time migration for the domain-cookie rollout: users logged in before it
 * hold a host-only sb-* cookie that would coexist with the new domain-wide
 * one. Returns raw Set-Cookie values that expire the host-only variant
 * (deletion must match the original cookie's attributes — i.e. no Domain).
 * Returns [] when no domain is configured or it doesn't apply to this host.
 * Remove after a release cycle.
 */
export function hostOnlyExpiryHeaders(cookieNames: string[], hostname?: string): string[] {
  if (!getSharedCookieOptions(hostname).domain) return []
  return cookieNames.map((name) => `${name}=; Path=/; Max-Age=0`)
}
